import { bookServiceApi } from '@/services/api';

// 1. TypeScript Interfaces

export interface Comment {
  review_id: number;
  book_id: number;
  user_id: number;
  parent_review_id: number | null;
  comment: string;
  rating: number;
  created_at: string | null;
  // When hierarchical is requested from backend
  replies?: Comment[];
  // Enriched user info from backend service
  name?: string;
  avatar_url?: string;
}

export interface PostCommentPayload {
  book_id: number;
  parent_review_id?: number | null; // null or undefined for top-level comments
  comment: string;
  rating?: number; // Optional when replying
}

export interface Pagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface CommentsPaginatedResponse {
  comments: Comment[];
  pagination: Pagination;
}

// 2. API Function

/**
 * Posts a new comment or review for a book.
 * @param payload - The data for the new comment.
 * @returns The newly created comment.
 */
export const postComment = async (payload: PostCommentPayload): Promise<Comment> => {
  if (!payload.book_id || !payload.comment) {
    throw new Error('Book ID and comment are required.');
  }

  try {
    const response = await bookServiceApi.post<Comment>('/comments/', payload);
    return response.data;
  } catch (error) {
    console.error('Error posting comment:', error);
    throw new Error('Failed to post comment. Please make sure you are logged in and try again.');
  }
};

export interface GetCommentsByBookParams {
  page?: number;
  limit?: number;
}

/**
 * Fetches all comments for a given book with pagination.
 * @param bookId - The ID of the book.
 * @param params - Pagination parameters.
 * @returns A promise that resolves to paginated comments.
 */
export const getCommentsByBook = async (bookId: number, params: GetCommentsByBookParams = {}): Promise<CommentsPaginatedResponse> => {
  if (!bookId) {
    throw new Error('A book ID must be provided.');
  }

  try {
    const { page = 1, limit = 10 } = params;
    const response = await bookServiceApi.get<CommentsPaginatedResponse>(
      `/comments/book/${bookId}?page=${page}&limit=${limit}&include_replies=true`
    );
    
    // Handle both old format (array) and new format (paginated)
    if (Array.isArray(response.data)) {
      return {
        comments: response.data,
        pagination: {
          page: 1,
          limit: response.data.length,
          total_count: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        }
      };
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching comments for book ${bookId}:`, error);
    throw new Error('Failed to fetch comments. Please try again later.');
  }
};

// 3. New Interfaces & Functions for Comment Statistics

export interface CommentStats {
  book_id: number;
  total_comments: number;
  total_ratings: number;
  average_rating: number;
  rating_distribution: {
    '1': number;
    '2': number;
    '3': number;
    '4': number;
    '5': number;
  };
  has_comments: boolean;
}

/**
 * Fetches comment and rating statistics for a given book.
 * @param bookId - The ID of the book.
 * @returns A promise that resolves to the book's comment statistics.
 */
export const getBookCommentStats = async (bookId: number): Promise<CommentStats> => {
  if (!bookId) {
    throw new Error('A book ID must be provided.');
  }

  try {
    const response = await bookServiceApi.get<CommentStats>(`/comments/book/${bookId}/stats`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching comment stats for book ${bookId}:`, error);
    throw new Error('Failed to fetch comment statistics. Please try again later.');
  }
};
